import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { fiscalYearMonths } from '@fint/validation';
import { api, ApiError, qs } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { usePermissions } from '@/hooks/usePermissions';
import { useBaseCurrency, useFiscalYearStartMonth } from '@/hooks/useOrgSettings';
import { financialYearOf, financialYearPeriod } from '@/lib/dates';
import { PageHeader } from '@/app/DashboardLayout';
import {
  Button, Card, CardContent, CardHeader, CardTitle, ErrorState, Input, Money, SkeletonTable,
  TBody, TD, TH, THead, TR, Table,
} from '@/features/common/ui';

interface TargetRow {
  id: string;
  metric: string;
  periodType: string;
  periodStart: string;
  amount: string;
  currency: string;
}

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const monthLabel = (ym: string) => {
  const [year, month] = ym.split('-').map(Number);
  return `${MONTH_NAMES[month - 1]} ${year}`;
};

/**
 * The monthly target the dashboard measures against.
 *
 * ── WHY THE MEASURE IS NOT A CHOICE ON THIS SCREEN ──
 * The owner settled it: the goal is MONEY IN. The column stores the metric
 * anyway, because the two are different numbers and a variance whose meaning is
 * assumed rather than recorded is one nobody can interpret in six months - but
 * offering a radio here would be offering a decision that has been made, and
 * every screen showing the variance says "vs goal (money in)" already.
 *
 * ── WHY TWELVE ROWS AND NOT ONE NUMBER ──
 * A target moves. A business that raises it from 2,00,000 to 3,00,000 in
 * October must not have April-to-September's variance restated to make six good
 * months look bad, which is exactly what one number in a settings blob does,
 * retroactively, with no record that it moved. "Apply to every month" writes
 * the twelve rows in one request and one audit event; October's raise is then
 * an edit to October onward and nothing before it.
 *
 * The annual figure is derived here rather than stored: two stored numbers that
 * must agree will eventually disagree, and the one on screen will be the wrong
 * one.
 */
export function GoalsPage() {
  const org = useActiveOrg();
  const perms = usePermissions();
  const currency = useBaseCurrency();
  const fyStartMonth = useFiscalYearStartMonth();
  const queryClient = useQueryClient();

  const [fyOffset, setFyOffset] = useState(0);
  const fy = useMemo(() => {
    const thisYear = financialYearOf(new Date().toISOString().slice(0, 10), fyStartMonth).startYear;
    return financialYearPeriod(thisYear + fyOffset, fyStartMonth);
  }, [fyStartMonth, fyOffset]);

  const months = useMemo(() => fiscalYearMonths(fy.from), [fy.from]);

  const [draft, setDraft] = useState<Record<string, string>>({});
  const [bulk, setBulk] = useState('');

  const orgId = org?.id;
  const targets = useQuery({
    queryKey: ['targets', orgId, fy.from],
    enabled: !!orgId,
    queryFn: () =>
      api.get<{ items: TargetRow[] }>(`/targets${qs({ from: fy.from, to: fy.to, metric: 'income' })}`),
  });

  const stored = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of targets.data?.items ?? []) map.set(row.periodStart.slice(0, 7), row.amount);
    return map;
  }, [targets.data]);

  const valueFor = (ym: string) => draft[ym] ?? stored.get(ym) ?? '';

  const save = useMutation({
    mutationFn: (entries: Array<{ periodStart: string; amount: string }>) =>
      api.put<{ items: TargetRow[] }>('/targets', { metric: 'income', periodType: 'month', entries }),
    onSuccess: () => {
      setDraft({});
      void queryClient.invalidateQueries({ queryKey: ['targets', orgId] });
      // The dashboard's variance column comes from these.
      void queryClient.invalidateQueries({ queryKey: ['dashboard-overview', orgId] });
      void queryClient.invalidateQueries({ queryKey: ['dashboard-breakdown', orgId] });
    },
  });

  const yearTotal = months.reduce((sum, ym) => {
    const raw = valueFor(ym);
    return raw === '' ? sum : sum + Math.round(Number(raw) * 100);
  }, 0);

  const dirty = Object.keys(draft).length > 0;
  const canEdit = perms.can('settings', 'manage');
  const error = save.error instanceof ApiError ? save.error : undefined;

  const submit = () => {
    const entries = months
      .map((ym) => ({ periodStart: `${ym}-01`, amount: valueFor(ym) }))
      .filter((entry) => entry.amount !== '');
    if (entries.length > 0) save.mutate(entries);
  };

  return (
    <>
      <PageHeader
        title="Goals"
        subtitle="A monthly target, so the dashboard can tell you whether you are ahead or behind."
      />
      {/* The same gutter every other screen has. Without it the content sat
          flush against the sidebar, on these four pages and no others. */}
      <div className="space-y-4 px-4 pb-10 sm:px-5">
        <Card>
          <CardHeader>
            <CardTitle>Monthly income target · {fy.label}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setFyOffset((n) => n - 1)}>
                Previous year
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setFyOffset((n) => n + 1)}>
                Next year
              </Button>
            </div>

            {canEdit ? (
              <div className="flex flex-wrap items-end gap-2">
                <Input
                  label="Set the same target for every month"
                  inputMode="decimal"
                  value={bulk}
                  onChange={(event) => setBulk(event.target.value)}
                  hint="One click, twelve rows. Change any of them below afterwards."
                />
                <Button
                  type="button"
                  variant="outline"
                  disabled={bulk.trim() === ''}
                  onClick={() => {
                    const next: Record<string, string> = {};
                    for (const ym of months) next[ym] = bulk.trim();
                    setDraft(next);
                  }}
                >
                  Apply to all twelve
                </Button>
              </div>
            ) : null}

            {targets.isPending ? <SkeletonTable rows={6} /> : null}
            {targets.isError ? (
              <ErrorState error={targets.error} onRetry={() => { void targets.refetch(); }} />
            ) : null}

            {targets.isSuccess ? (
              <Table>
                <THead>
                  <TR>
                    <TH>Month</TH>
                    <TH className="text-right">Target</TH>
                  </TR>
                </THead>
                <TBody>
                  {months.map((ym) => (
                    <TR key={ym}>
                      <TD>{monthLabel(ym)}</TD>
                      <TD className="text-right">
                        <input
                          aria-label={`Target for ${monthLabel(ym)}`}
                          inputMode="decimal"
                          disabled={!canEdit}
                          value={valueFor(ym)}
                          onChange={(event) =>
                            setDraft((current) => ({ ...current, [ym]: event.target.value }))
                          }
                          placeholder="No target"
                          className="h-9 w-36 rounded-md border border-input bg-background px-2 text-right text-sm tabular-nums disabled:opacity-60"
                        />
                      </TD>
                    </TR>
                  ))}
                  <TR className="font-medium">
                    <TD>For the year</TD>
                    <TD className="text-right tabular-nums">
                      <Money value={(yearTotal / 100).toFixed(2)} currency={currency} />
                    </TD>
                  </TR>
                </TBody>
              </Table>
            ) : null}

            {error ? (
              // Announced, not just rendered - the button that caused it is at
              // the bottom of a twelve-row table.
              <p role="alert" className="text-sm text-destructive">
                {error.message}
              </p>
            ) : null}

            {canEdit ? (
              <div className="flex items-center gap-2">
                <Button onClick={submit} disabled={!dirty || save.isPending}>
                  {save.isPending ? 'Saving…' : 'Save targets'}
                </Button>
                {dirty ? (
                  <Button variant="ghost" onClick={() => setDraft({})}>
                    Discard changes
                  </Button>
                ) : null}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Only an administrator can change the target.
              </p>
            )}

            <p className="text-xs text-muted-foreground">
              A month you leave blank has no target, and the dashboard says so rather than showing a
              variance against zero - which would make every month look like a triumph.
            </p>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
